// @flow

import actionCreators from "@modules/profile/actionCreator";
import http from "@http";
import type { Action } from "redux-action";
import {
  GetServiceDirectoryCategories,
  GetServiceDirectorySubCategories,
  GetServiceDirectoryTopicViewRequest,
} from "@modules/profile/constants/actions";
import { all, call, put, takeEvery } from "redux-saga/effects";
import type {
  ServiceDirectorySubCategory,
  ServiceDirectoryCategory,
  ServiceDirectoryTopic,
} from "@modules/profile/reducer/service-directory/types";
import { getUserClientId } from "@modules/account/selectors";

export function* onGetServiceDirectoryCategories(): Generator<
  *,
  void,
  { data: Array<ServiceDirectoryCategory> },
> {
  try {
    const response = yield call(
      http.get,
      "api/v2/service-admin/service-config/absence-management/wellbeing/category",
    );

    yield put(actionCreators.GetServiceDirectoryCategoriesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetServiceDirectoryCategoriesError(e.response));
  }
}

type SubServicesGetRequest = $Exact<{
  categoryId: number,
  userClientId: number,
}>;

export function* onGetServiceDirectorySubCategories({
  payload,
}: Action<SubServicesGetRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.categoryId || !payload.userClientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: categoriesData } = yield call(
      http.get,
      "api/v2/service-admin/service-config/absence-management/wellbeing/service-category/service-types",
      {
        params: {
          categoryId: payload.categoryId,
        },
      },
    );

    const ids = categoriesData.map(el => `service_type_id:${el.id}`).join();
    const { data: topicsData } = yield call(
      http.get,
      `api/v2/service-admin/client/${payload.userClientId}/service-config/absence-management/wellbeing/service`,
      {
        params: {
          includePublic: true,
          search: ids,
          isActive: true,
        },
      },
    );

    const data = categoriesData.map(category => {
      const categoryTopic = topicsData.content.filter(el =>
        el.content.wellbeingServiceTypes.find(type => type.id === category.id),
      );

      return {
        ...category,
        topics: categoryTopic,
      };
    });

    yield put(actionCreators.GetServiceDirectorySubCategoriesSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetServiceDirectorySubCategoriesError(e.response));
  }
}

type TopicViewRequest = $Exact<{
  topicId: number,
  userClientId: number,
}>;

export function* onGetServiceDirectoryTopicViewRequest({
  payload,
}: Action<TopicViewRequest>): Generator<*, void, { data: Array<ServiceDirectoryCategory> }> {
  try {
    if (!payload || !payload.topicId || !payload.userClientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.get,
      `api/v2/service-admin/client/${payload.userClientId}/service-config/absence-management/wellbeing/service/${payload.topicId}`,
      {
        params: {
          includePublic: true,
        },
      },
    );
  } catch (e) {
    yield put(actionCreators.GetServiceDirectoryTopicViewRequestError(e.response));
  }
}

export default function* ServiceDirectorySaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetServiceDirectoryCategories, onGetServiceDirectoryCategories),
    takeEvery(GetServiceDirectorySubCategories, onGetServiceDirectorySubCategories),
    takeEvery(GetServiceDirectoryTopicViewRequest, onGetServiceDirectoryTopicViewRequest),
  ]);
}
